import create from 'zustand';

interface Notification {
  id: string;
  message: string;
  timestamp: string; // ISO string for consistent formatting
  isNew: boolean; // Indicates whether the notification is unread
  type?: string; // Type of notification (e.g., 'game_analysis', 'info')
  requestId?: string; // Request ID for tracking
  userId?: string; // User ID associated with the notification
}

interface NotificationStore {
  notifications: Notification[];
  gameReviewNotifications: {[id: string]: any[]}; // Keyed by id, value is array of moves
  refetchStudy: boolean;
  setRefetchStudy: (refetchStudy: boolean) => void;
  addNotification: (notification: Notification) => void;
  addGameReviewNotification: (notification: Notification) => void; // New
  updateGameReviewNotification: (notification: Notification) => void; // New
  clearGameReviewNotifications: () => void; // New
  markAllAsRead: () => void;
  removeNotification: (id: string) => void;
  hasNewNotifications: () => boolean;
}

export const useNotificationStore = create<NotificationStore>((set, get) => ({
  notifications: [],
  gameReviewNotifications: {}, // Keyed by id
  refetchStudy: false,
  setRefetchStudy: refetchStudy => set({refetchStudy: refetchStudy}),
  addNotification: notification =>
    set(state => {
      const exists = state.notifications.some(
        notif => notif.id === notification.id,
      );
      if (exists) return state;
      return {
        notifications: [{...notification, isNew: true}, ...state.notifications],
      };
    }),
  addGameReviewNotification: notification =>
    set(state => {
      const move = notification.message;

      // Extract game_uuid from the move data to use as the key
      let gameUuid = notification.id; // fallback to notification id

      if (move && typeof move === 'object') {
        // Try to get game_uuid from the move object
        if (move.game_uuid) {
          gameUuid = move.game_uuid;
        } else if (move.uuid) {
          gameUuid = move.uuid;
        }
      }

      const prevMoves = state.gameReviewNotifications[gameUuid] || [];

      // Ensure moves are accumulated in a single array for the same game_uuid
      let newMoves = [...prevMoves];

      if (move && typeof move === 'object') {
        // Check if this move already exists (by move_index if available)
        // const existingMoveIndex =
        //   move.move_index !== undefined
        //     ? prevMoves.findIndex(m => m.move_index === move.move_index)
        //     : -1;

        // if (existingMoveIndex === -1) {
        // Add new move if it doesn't exist
        newMoves.push(move);
        // } else {
        //   // Update existing move
        //   newMoves[existingMoveIndex] = move;
        // }

        // Sort by move_index to maintain order
        newMoves.sort((a, b) => (a.move_index || 0) - (b.move_index || 0));
      } else {
        // For non-object moves, just add to array
        newMoves.push(move);
      }

      console.log(
        'addGameReviewNotification - Accumulating moves for game_uuid:',
        gameUuid,
        {
          totalMoves: newMoves.length,
          gameReviewNotifications: {
            ...state.gameReviewNotifications,
            [gameUuid]: newMoves,
          },
        },
      );

      return {
        gameReviewNotifications: {
          ...state.gameReviewNotifications,
          [gameUuid]: newMoves,
        },
      };
    }),
  updateGameReviewNotification: notification =>
    set(state => {
      const move = notification.message;

      // Extract game_uuid from the move data to use as the key
      let gameUuid = notification.id; // fallback to notification id

      if (move && typeof move === 'object') {
        // Try to get game_uuid from the move object
        if (move.game_uuid) {
          gameUuid = move.game_uuid;
        } else if (move.uuid) {
          gameUuid = move.uuid;
        }
      }

      const prevMoves = state.gameReviewNotifications[gameUuid] || [];

      // Ensure moves are accumulated in a single array for the same game_uuid
      let newMoves = [...prevMoves];

      if (move && typeof move === 'object') {
        // Check if this move already exists (by move_index if available)
        // const existingMoveIndex =
        //   move.move_index !== undefined
        //     ? prevMoves.findIndex(m => m.move_index === move.move_index)
        //     : -1;

        // if (existingMoveIndex === -1) {
        // Add new move if it doesn't exist
        newMoves.push(move);
        // } else {
        //   // Update existing move
        //   newMoves[existingMoveIndex] = move;
        // }

        // Sort by move_index to maintain order
        // newMoves.sort((a, b) => (a.move_index || 0) - (b.move_index || 0));
      } else {
        // For non-object moves, just add to array
        newMoves.push(move);
      }

      console.log(
        'updateGameReviewNotification - Accumulating moves for game_uuid:',
        gameUuid,
        JSON.stringify(
          {
            totalMoves: newMoves.length,
            gameReviewNotifications: {
              ...state.gameReviewNotifications,
              [gameUuid]: newMoves,
            },
          },
          null,
          2,
        ),
      );

      return {
        gameReviewNotifications: {
          ...state.gameReviewNotifications,
          [gameUuid]: newMoves,
        },
      };
    }),
  clearGameReviewNotifications: () => set({gameReviewNotifications: {}}),
  markAllAsRead: () =>
    set(state => ({
      notifications: state.notifications.map(notif => ({
        ...notif,
        isNew: false,
      })),
    })),
  removeNotification: id =>
    set(state => ({
      notifications: state.notifications.filter(notif => notif.id !== id),
    })),
  hasNewNotifications: () => get().notifications.some(notif => notif.isNew),
}));
